import { Currency, Token } from '@uniswap/sdk-core'
import { AutoColumn } from 'components/Column'
import CurrencyLogo from 'components/Logo/CurrencyLogo'
import { COMMON_BASES_TOKENLIST, PULSECHAIN_LIST } from 'constants/lists'
import { COMMON_BASES } from 'constants/routing'
import { useFavorites } from 'hooks/useFavorites'
import { useTokenInfoFromActiveList } from 'hooks/useTokenInfoFromActiveList'
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react'
import { ChevronLeft, ChevronRight, Star, Trash2 } from 'react-feather'
import { Text } from 'rebass'
import styled, { css, keyframes, useTheme } from 'styled-components/macro'
import { currencyId } from 'utils/currencyId'

const COLS = 5
const ROWS = 3
const PAGE_SIZE = COLS * ROWS
const CHIP_H = 28
const COL_GAP = 5
const ROW_GAP = 5
const ROW_MIN_H = CHIP_H * ROWS + ROW_GAP * (ROWS - 1)
const PANEL_GAP = 6
const SIDE_COL = 48
const SIDE_BTN_H = 48
const SIDE_BTN_GAP = 4

const MobileWrapper = styled(AutoColumn)`
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall``};
`

const Row = styled.div`
  display: grid;
  grid-template-columns: minmax(0, 1fr) 26px;
  column-gap: ${PANEL_GAP}px;
  align-items: start;
  width: 100%;
  max-width: 100%;
  box-sizing: border-box;
  min-height: ${ROW_MIN_H}px;
`

const slideInFromRight = keyframes`
  from { opacity: .6; transform: translateX(8px); }
  to   { opacity: 1;  transform: translateX(0); }
`
const slideInFromLeft = keyframes`
  from { opacity: .6; transform: translateX(-8px); }
  to   { opacity: 1;  transform: translateX(0); }
`

const ChipsGrid = styled.div<{ $anim?: 'next' | 'prev' | null }>`
  overflow: hidden;
  box-sizing: border-box;
  min-width: 0;
  display: grid;
  grid-template-columns: repeat(${COLS}, minmax(0, 1fr));
  grid-auto-rows: ${CHIP_H}px;
  column-gap: ${COL_GAP}px;
  row-gap: ${ROW_GAP}px;
  min-height: ${ROW_MIN_H}px;
  width: 100%;
  ${({ $anim }) =>
    $anim === 'next'
      ? css`
          animation: ${slideInFromRight} 160ms ease both;
        `
      : $anim === 'prev'
      ? css`
          animation: ${slideInFromLeft} 160ms ease both;
        `
      : null}
`

const ChipsScroll = styled.div`
  min-height: ${ROW_MIN_H}px;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
  display: flex;
  gap: ${PANEL_GAP}px;
  scroll-snap-type: x mandatory;
  -webkit-overflow-scrolling: touch;

  &::-webkit-scrollbar {
    display: none;
  }
`

const ChipsPage = styled(ChipsGrid)`
  flex: 0 0 100%;
  scroll-snap-align: start;
`

const WrapGrid = styled.div`
  display: grid;
  grid-template-columns: repeat(auto-fit, minmax(80px, 1fr));
  gap: ${ROW_GAP}px ${COL_GAP}px;
  align-items: center;
  width: 100%;
`

const BaseWrapper = styled.div<{ disable?: boolean }>`
  min-width: 0;
  height: ${CHIP_H}px;
  border-radius: 8px;
  border: 1px solid ${({ theme, disable }) => (disable ? theme.accentActive : theme.backgroundOutline)};
  display: flex;
  align-items: center;
  justify-content: center;
  padding: 0 2px;
  gap: 2px;
  background-color: ${({ theme, disable }) => (disable ? theme.accentActiveSoft : 'transparent')};
  color: ${({ theme, disable }) => (disable ? theme.accentActive : 'inherit')};
  font-size: 13px;
  font-weight: 600;
  //line-height: 1;
  :hover {
    cursor: ${({ disable }) => (!disable ? 'pointer' : 'default')};
    background-color: ${({ theme, disable }) => (disable ? theme.accentActiveSoft : theme.hoverDefault)};
  }
  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    font-size: 11px;
  `}
`

const Placeholder = styled.div`
  height: ${CHIP_H}px;
`

const SideStack = styled.div`
  display: flex;
  flex-direction: column;
  gap: ${SIDE_BTN_GAP}px;
  width: ${SIDE_COL}px;
  height: ${ROW_MIN_H}px;
`

const SideBtn = styled.button<{ $h?: number }>`
  height: ${({ $h }) => ($h ? `${$h}px` : `${SIDE_BTN_H}px`)};
  width: 100%;
  border: none;
  outline: none;
  background: transparent;
  padding: 0;
  display: flex;
  align-items: center;
  justify-content: flex-end;
  cursor: pointer;
`

const BtnBody = styled.div<{ $bg: string; $color?: string; $widthPct: number }>`
  height: 100%;
  width: ${({ $widthPct }) => `${$widthPct}%`};
  border-radius: 6px 0 0 6px;
  display: inline-flex;
  align-items: center;
  justify-content: center;
  gap: 1px;
  font-size: 11px;
  font-weight: 800;
  letter-spacing: 0.3px;
  box-shadow: 0 2px 10px rgba(0, 0, 0, 0.22);
  color: ${({ $color }) => $color || '#fff'};
  background: ${({ $bg }) => $bg};
  & svg {
    stroke: currentColor;
  }
  &:hover {
    filter: brightness(1.06);
  }
  &:active {
    transform: translateY(0.3px);
  }
`

const VerticalLabel = styled.span`
  writing-mode: vertical-rl;
  transform: rotate(180deg);
  font-size: 10.5px;
  font-weight: 800;
  letter-spacing: 0.6px;
  text-transform: uppercase;
  line-height: 1;
  user-select: none;
  pointer-events: none;
  opacity: 0.95;
`

const ClearChip = styled.button`
  height: ${CHIP_H}px;
  padding: 0 8px;
  display: inline-flex;
  align-items: center;
  text-align: center;
  gap: 6px;
  border-radius: 8px;
  border: 1px solid ${({ theme }) => theme.deprecated_bg2};
  background: ${({ theme }) => theme.deprecated_bg2};
  color: ${({ theme }) => theme.textPrimary};
  cursor: pointer;
  &:hover {
    filter: brightness(1.05);
  }
`

const EmptyNotice = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  min-height: ${ROW_MIN_H}px;
  padding: 10px 12px;
  //text-align: center;
  border: 1px dashed ${({ theme }) => theme.backgroundOutline};
  border-radius: 8px;
  //background: ${({ theme }) => theme.deprecated_bg2};
  color: ${({ theme }) => theme.textPrimary};

  ${({ theme }) => theme.deprecated_mediaWidth.deprecated_upToSmall`
    padding: 8px 10px;
  `}
`

type CommonBasesProps = {
  chainId?: number
  selectedCurrency?: Currency | null
  onSelect: (currency: Currency) => void
  searchQuery?: string
  isAddressSearch?: string | false
}

let __cachedCommonBases: any | null = null
let __cachedTokenList: any | null = null

function extractEntries(remote: any, chainId?: number): string[] {
  if (Array.isArray(remote?.list))
    return remote.list.map((e: any) => (typeof e === 'string' ? e : e?.address)).filter(Boolean)
  if (chainId && Array.isArray(remote?.chains?.[String(chainId)]))
    return remote.chains[String(chainId)].map((e: any) => (typeof e === 'string' ? e : e?.address)).filter(Boolean)
  return []
}

export default function CommonBases({ chainId, onSelect, selectedCurrency }: CommonBasesProps) {
  const theme = useTheme()
  const [page, setPage] = useState(0)
  const [mode, setMode] = useState<'normal' | 'favorites'>('normal')
  const { favorites, clearAll } = useFavorites()
  const scrollRef = useRef<HTMLDivElement | null>(null)
  const isProgrammaticScroll = useRef(false)

  const [remote, setRemote] = useState<any | null>(__cachedCommonBases)
  useEffect(() => {
    let alive = true
    async function load() {
      try {
        if (!__cachedCommonBases) {
          const r = await fetch(COMMON_BASES_TOKENLIST, { cache: 'no-store' })
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          __cachedCommonBases = await r.json()
        }
        if (alive) setRemote(__cachedCommonBases)
      } catch {
        /* noop */
      }
    }
    if (!remote) load()
    return () => {
      alive = false
    }
  }, [remote])

  const [tokenList, setTokenList] = useState<any | null>(__cachedTokenList)
  useEffect(() => {
    let alive = true
    async function load() {
      try {
        if (!__cachedTokenList) {
          const r = await fetch(PULSECHAIN_LIST, { cache: 'no-store' })
          if (!r.ok) throw new Error(`HTTP ${r.status}`)
          __cachedTokenList = await r.json()
        }
        if (alive) setTokenList(__cachedTokenList)
      } catch {
        /* noop */
      }
    }
    if (!tokenList) load()
    return () => {
      alive = false
    }
  }, [tokenList])

  const basesAll: Currency[] = useMemo(() => {
    if (!chainId || !remote || !tokenList) return []
    const entries: string[] = extractEntries(remote, chainId)
    if (!entries.length) return []
    const nativeCandidate =
      (COMMON_BASES[chainId] || []).find((c: any) => (c as any)?.isNative) ?? (COMMON_BASES[chainId] || [])[0]
    const out: Currency[] = []
    for (const entry of entries) {
      if (entry === 'NATIVE') {
        if (nativeCandidate) out.push(nativeCandidate)
        continue
      }
      const t = (tokenList.tokens || []).find(
        (x: any) => x.chainId === chainId && x.address?.toLowerCase() === entry.toLowerCase()
      )
      if (t) out.push(new Token(t.chainId, t.address, t.decimals, t.symbol, t.name))
    }
    return out.length ? out : COMMON_BASES[chainId] ?? []
  }, [chainId, remote, tokenList])

  const favoriteCurrencies: Currency[] = useMemo(() => {
    if (!chainId || !tokenList) return []
    const arr: Currency[] = []
    const nativeCandidate =
      (COMMON_BASES[chainId] || []).find((c: any) => (c as any)?.isNative) ?? (COMMON_BASES[chainId] || [])[0]
    favorites.forEach((id) => {
      if (id === 'NATIVE' && nativeCandidate) {
        arr.push(nativeCandidate)
      } else {
        const t = (tokenList.tokens || []).find(
          (x: any) => x.chainId === chainId && x.address?.toLowerCase() === id.toLowerCase()
        )
        if (t) arr.push(new Token(t.chainId, t.address, t.decimals, t.symbol, t.name))
      }
    })
    return arr
  }, [favorites, chainId, tokenList])

  const normalPages = useMemo(() => {
    if (!basesAll.length) return []
    const pages: Currency[][] = []
    for (let i = 0; i < basesAll.length; i += PAGE_SIZE) {
      pages.push(basesAll.slice(i, i + PAGE_SIZE))
    }
    return pages.length ? pages : [[]]
  }, [basesAll])

  const totalPages = normalPages.length || 1
  const showMore = totalPages > 1
  const cappedPage = Math.min(page, totalPages - 1)
  const pagesToRender = normalPages.length ? normalPages : ([[]] as Currency[][])

  const scrollToPage = useCallback((target: number) => {
    const el = scrollRef.current
    if (!el) return
    const step = el.clientWidth + PANEL_GAP
    isProgrammaticScroll.current = true
    el.scrollTo({ left: target * step, behavior: 'smooth' })
    window.setTimeout(() => {
      isProgrammaticScroll.current = false
    }, 250)
  }, [])

  const goToPage = useCallback(
    (target: number) => {
      const next = Math.max(0, Math.min(target, totalPages - 1))
      setPage(next)
      scrollToPage(next)
    },
    [scrollToPage, totalPages]
  )

  useEffect(() => {
    if (!showMore) {
      if (page !== 0) setPage(0)
      scrollToPage(0)
    }
  }, [showMore, page, scrollToPage])

  useEffect(() => {
    if (page >= totalPages) {
      setPage(0)
      scrollToPage(0)
    }
  }, [page, totalPages, scrollToPage])

  useEffect(() => {
    if (mode === 'favorites') {
      setPage(0)
      scrollToPage(0)
    }
  }, [mode, scrollToPage])

  const handleScroll = useCallback(() => {
    const el = scrollRef.current
    if (!el || !showMore || isProgrammaticScroll.current) return
    const step = el.clientWidth + PANEL_GAP
    const computed = Math.round(el.scrollLeft / step)
    const next = Math.max(0, Math.min(computed, totalPages - 1))
    setPage((prev) => (prev === next ? prev : next))
  }, [showMore, totalPages])

  const visibleFavorites = useMemo(() => favoriteCurrencies.slice(0, PAGE_SIZE), [favoriteCurrencies])
  const isLast = cappedPage >= totalPages - 1
  const moreBg = `linear-gradient(135deg, ${theme.accentActive} 0%, ${theme.accentActiveSoft} 100%)`

  return (
    <MobileWrapper gap="md">
      <Row>
        {mode === 'favorites' ? (
          visibleFavorites.length === 0 ? (
            <EmptyNotice role="status" aria-live="polite">
              <Text fontWeight={700} fontSize={13}>
                Your favorites list is empty. Use the star in the list below to add tokens.
              </Text>
            </EmptyNotice>
          ) : (
            <WrapGrid>
              {visibleFavorites.map((item) => (
                <BaseWrapper
                  key={currencyId(item)}
                  tabIndex={0}
                  disable={selectedCurrency?.equals(item)}
                  onKeyPress={(e) => !selectedCurrency?.equals(item) && e.key === 'Enter' && onSelect(item)}
                  onClick={() => !selectedCurrency?.equals(item) && onSelect(item)}
                >
                  <CurrencyLogoFromList currency={item} />
                  <Text fontWeight={600} fontSize={13}>
                    {item.symbol}
                  </Text>
                </BaseWrapper>
              ))}
              <ClearChip onClick={clearAll}>
                <Trash2 size={14} />
                <Text fontSize={11} fontWeight={800}>
                  Clear
                </Text>
              </ClearChip>
            </WrapGrid>
          )
        ) : (
          <ChipsScroll ref={scrollRef} onScroll={handleScroll}>
            {pagesToRender.map((items, idx) => (
              <ChipsPage key={`page-${idx}`} $anim={null}>
                {items.map((item) => (
                  <BaseWrapper
                    key={currencyId(item)}
                    tabIndex={0}
                    disable={selectedCurrency?.equals(item)}
                    onKeyPress={(e) => !selectedCurrency?.equals(item) && e.key === 'Enter' && onSelect(item)}
                    onClick={() => !selectedCurrency?.equals(item) && onSelect(item)}
                  >
                    <CurrencyLogoFromList currency={item} />
                    <Text fontWeight={600} fontSize={13}>
                      {item.symbol}
                    </Text>
                  </BaseWrapper>
                ))}
                {items.length < PAGE_SIZE &&
                  Array.from({ length: PAGE_SIZE - items.length }).map((_, i) => (
                    <Placeholder key={`ph-${idx}-${i}`} />
                  ))}
              </ChipsPage>
            ))}
          </ChipsScroll>
        )}

        <SideStack>
          <SideBtn
            $h={64}
            aria-label={isLast ? 'Back to first tokens' : 'Next tokens'}
            title={isLast ? 'Less' : 'More'}
            onClick={() => {
              if (!showMore) return
              goToPage(isLast ? 0 : cappedPage + 1)
            }}
            disabled={mode === 'favorites' || !showMore}
          >
            <BtnBody $bg={moreBg} $widthPct={92} style={{ opacity: mode === 'favorites' || !showMore ? 0.4 : 1 }}>
              <VerticalLabel>{isLast ? 'Less' : 'More'}</VerticalLabel>
              {isLast ? <ChevronLeft size={16} /> : <ChevronRight size={16} />}
            </BtnBody>
          </SideBtn>

          <SideBtn
            $h={30}
            aria-label={mode === 'favorites' ? 'Close favorites' : 'Open favorites'}
            title="Favorites"
            onClick={() => setMode((m) => (m === 'favorites' ? 'normal' : 'favorites'))}
          >
            <BtnBody $bg="#FFD54F" $color="#5A4100" $widthPct={60}>
              <Star size={16} />
            </BtnBody>
          </SideBtn>
        </SideStack>
      </Row>
    </MobileWrapper>
  )
}

function CurrencyLogoFromList({ currency }: { currency: Currency }) {
  const token = useTokenInfoFromActiveList(currency)
  return <CurrencyLogo currency={token} size="16px" />
}
